/**
 * The Lilia menu's pinned tools, in the user's order (navigation handoff §2:
 * drag to reorder, right-click to unpin).
 *
 * Stored per user in preferences (`pinnedTools`), so the order follows the
 * account across browsers. Mirrored in localStorage so a change shows at once
 * and survives an API that doesn't know the field yet.
 */
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UserPreferences } from "@/lib/api";
import { usePreferences } from "@/hooks/usePreferences";
import { DEFAULT_PINNED, TOOLS, type ToolKey } from "./tools";

const LOCAL = "lilia.pinnedTools.v1";
const KNOWN = new Set<string>(TOOLS.map((t) => t.key));

/** Known tools only, each once, in the order given. */
export function cleanPinned(keys: readonly string[] | null | undefined): ToolKey[] | null {
  if (!Array.isArray(keys)) return null;
  const seen = new Set<string>();
  return keys.filter((k) => KNOWN.has(k) && !seen.has(k) && (seen.add(k), true)) as ToolKey[];
}

/** `from` moved to where `to` is. */
export function movePinned(keys: ToolKey[], from: ToolKey, to: ToolKey): ToolKey[] {
  const i = keys.indexOf(from);
  const j = keys.indexOf(to);
  if (i < 0 || j < 0 || i === j) return keys;
  const next = [...keys];
  next.splice(i, 1);
  next.splice(j, 0, from);
  return next;
}

function readLocal(): ToolKey[] | null {
  try {
    const raw = window.localStorage.getItem(LOCAL);
    return raw ? cleanPinned(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function usePinnedTools() {
  const { preferences, updatePreferences } = usePreferences();
  const queryClient = useQueryClient();
  const [local, setLocal] = useState<ToolKey[] | null>(readLocal);

  // The account's order wins; this browser's copy stands in when the account
  // has none (never changed, or an API that doesn't store it yet).
  const pinned = useMemo<ToolKey[]>(
    () => cleanPinned(preferences?.pinnedTools) ?? local ?? DEFAULT_PINNED,
    [local, preferences?.pinnedTools],
  );

  const setPinned = useCallback((keys: ToolKey[]) => {
    setLocal(keys);
    try { window.localStorage.setItem(LOCAL, JSON.stringify(keys)); } catch { /* the account's copy still holds */ }
    // Shown at once; the PUT's answer then replaces it.
    queryClient.setQueryData<UserPreferences | undefined>(["preferences"], (old) => (old ? { ...old, pinnedTools: keys } : old));
    updatePreferences({ pinnedTools: keys });
  }, [queryClient, updatePreferences]);

  return { pinned, setPinned };
}
